import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import {
  clearLicenseExpired,
  getLicenseExpiredReturnTo,
  isLicenseExpiredFlagSet,
  isLicenseExpiredPayload,
} from "../../utils/licenseExpired";

const POLL_INTERVAL_MS = 10000;

const LicenseExpired = () => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);

  const baseUrl = useMemo(
    () =>
      (process.env.REACT_APP_API_BASE_URL || "http://localhost:50268").replace(
        /\/+$/,
        "",
      ),
    [],
  );

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("accessToken");
    const deviceToken =
      localStorage.getItem("edgeDeviceToken") ||
      process.env.REACT_APP_DEVICE_TOKEN ||
      "";

    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(deviceToken ? { "X-DEVICE-TOKEN": deviceToken } : {}),
    };
  }, []);

  const buildCheckUrl = useCallback(() => {
    const params = new URLSearchParams();
    params.append("since", new Date(0).toISOString());
    params.append("scope", "STORE");
    const storeId = localStorage.getItem("selectedStoreId");
    if (storeId) params.append("storeId", storeId);
    return `${baseUrl}/api/edge/realtime/sync?${params.toString()}`;
  }, [baseUrl]);

  const handleUnlock = useCallback(() => {
    clearLicenseExpired();
    const returnTo = getLicenseExpiredReturnTo();
    navigate(returnTo || "/login", { replace: true });
  }, [navigate]);

  const checkLicense = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    const deviceToken =
      localStorage.getItem("edgeDeviceToken") ||
      process.env.REACT_APP_DEVICE_TOKEN ||
      "";
    if (!token && !deviceToken) {
      return;
    }

    setIsChecking(true);
    try {
      const response = await fetch(buildCheckUrl(), {
        method: "GET",
        headers: getAuthHeaders(),
      });

      const contentType = response.headers.get("content-type") || "";
      let payload = null;
      if (contentType.includes("application/json")) {
        payload = await response.json();
      }

      if (payload && isLicenseExpiredPayload(payload)) {
        return;
      }

      if (response.ok) {
        handleUnlock();
      }
    } catch {
      // ignore transient failures
    } finally {
      setIsChecking(false);
      setLastCheckedAt(new Date().toISOString());
    }
  }, [buildCheckUrl, getAuthHeaders, handleUnlock]);

  useEffect(() => {
    if (!isLicenseExpiredFlagSet()) {
      const returnTo = getLicenseExpiredReturnTo();
      navigate(returnTo || "/login", { replace: true });
      return;
    }

    checkLicense();
    const interval = setInterval(checkLicense, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [checkLicense, navigate]);

  const lastCheckedLabel = lastCheckedAt
    ? new Date(lastCheckedAt).toLocaleTimeString()
    : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <Card className="w-full max-w-xl">
        <div className="space-y-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              License Expired
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Your Edge license is expired or not configured. Please contact
              your administrator to renew the subscription.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={checkLicense} disabled={isChecking}>
              {isChecking ? "Checking..." : "Retry"}
            </Button>
            {lastCheckedLabel && (
              <span className="text-xs text-slate-500">
                Last checked at {lastCheckedLabel}
              </span>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default LicenseExpired;
